import { RCU_LIMITS } from '../catalog/limits';
import { RAIL_TYPE_KEYS, RAIL_TYPE_SPECS } from '../catalog/railType';
import type { RailTypeKey } from '../catalog/railType';
import type { RcuConfig } from '../model';
import { moduleCount } from './capacity';
import { nextFreeRailAddress, railsOfType } from './lookup';

/** How full a rail type is, and whether another rail of it can be added. */
export interface RailTypeUsage {
  readonly type: RailTypeKey;
  readonly fitted: number;
  readonly perTypeLimit: number;
  /** Lowest unused address, or null when all are taken. */
  readonly nextAddress: number | null;
  readonly canAdd: boolean;
}

export const railTypeUsage = (config: RcuConfig, type: RailTypeKey): RailTypeUsage => {
  const fitted = railsOfType(config, type).length;
  const atTotalLimit = moduleCount(config) >= RCU_LIMITS.MAX_MODULES_TOTAL;
  return {
    type,
    fitted,
    perTypeLimit: RCU_LIMITS.MAX_RAILS_PER_TYPE,
    nextAddress: nextFreeRailAddress(config, type),
    canAdd:
      fitted < RCU_LIMITS.MAX_RAILS_PER_TYPE &&
      nextFreeRailAddress(config, type) !== null &&
      !atTotalLimit,
  };
};

/** Every rail type in catalog order, for the summary strip and add-rail picker. */
export const railTypeUsages = (config: RcuConfig): readonly RailTypeUsage[] =>
  RAIL_TYPE_KEYS.map((type) => railTypeUsage(config, type));

/** Rails fitted on this RCU, in type then address order. */
export const sortedRails = (config: RcuConfig) =>
  [...config.rails].sort(
    (left, right) =>
      RAIL_TYPE_KEYS.indexOf(left.type) - RAIL_TYPE_KEYS.indexOf(right.type) ||
      left.address - right.address,
  );

/** Total channels a rail contributes. */
export const channelsPerRail = (type: RailTypeKey): number => RAIL_TYPE_SPECS[type].channels;
